import { HttpContext, HttpParams } from '@angular/common/http';
import { EventEmitter, inject, Injectable } from '@angular/core';
import { ConfigValidationModel } from '@client-models/config-validation.model';
import { PartnerValidationModel } from '@client-models/partner-validation-data.model';
import { DigitizationSuiteFormModel } from '@client-models/request-digitalization-suite.model';
import { APP_CONSTANTS } from '@constants/app.constants';
import { LANDING_PAGE_AUTH_REQUIRED, USE_UTC } from '@constants/http-context-token';
import { LOCAL_STORAGE_CONSTANT } from '@constants/localstorage.constant';
import { CountryDbModel } from '@db-models/country.model';
import { IPartnerClientId, PartnerCloneRequest, PartnerDbModel, PartnerListParams, PartnerTranslationForm, SearchPartnersDbModel } from '@db-models/partner-db.model';
import { SetupAssistantStatusModel } from '@db-models/partnership-db.model';
import { SubscriptionModel } from '@db-models/subscribe.model';
import { WorkerDbModel } from '@db-models/worker-db.model';
import { FeatureService } from '@feature-services/feature.service';
import { PartnerSettingService } from '@feature-services/partner-setting.service';
import { TranslateService } from '@ngx-translate/core';
import { CryptoService } from '@util-services/crypto.service';
import { HelperService } from '@util-services/helper.service';
import { HttpClientService } from '@util-services/http-client.service';
import { LocalStorageService } from '@util-services/local-storage.service';
import { LoggerService } from '@util-services/logger.service';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PartnerService {

  private cryptoService = inject(CryptoService);
  private featureService = inject(FeatureService);
  private helperService = inject(HelperService);
  private httpClientService = inject(HttpClientService);
  private localStorageService = inject(LocalStorageService);
  private partnerSettingService = inject(PartnerSettingService);
  private translate = inject(TranslateService);

  partnerChangeEvent = new EventEmitter<PartnerDbModel>();
  partnerLoadEvent = new EventEmitter<PartnerDbModel>();
  betaFlagToggleEvent = new EventEmitter<number>();
  // Fires whenever the partner's subscription was just refreshed from BE
  // (e.g. after an upgrade) — banners / badges that cached subscription
  // state at init subscribe to this and re-read.
  subscriptionRefreshedEvent = new EventEmitter<SubscriptionModel>();

  updatePartnerRoles(partnerRoleModel: { partner_id: number, role_ids: number[] }): Observable<any> {
    return this.httpClientService.post('partners/roles', partnerRoleModel, {});
  }

  getPartners(): Observable<PartnerDbModel[]> {
    return this.httpClientService.get('partners', {});
  }

  searchPartners(query: string): Observable<PartnerDbModel[]> {
    const body: { query: string } = { query };
    return this.httpClientService.post(`partners/search`, body, {});
  }

  /**
   * Get paginated list of partners with health data and filter support
   * Supports both legacy (limit, offset) signature and new PartnerListParams object
   */
  getPartnerList(limitOrParams: PartnerListParams): Observable<SearchPartnersDbModel> {
    let httpParams: HttpParams;
    httpParams = new HttpParams()
      .set('limit', limitOrParams.limit.toString())
      .set('offset', limitOrParams.offset.toString());

    if (limitOrParams.query) {
      httpParams = httpParams.set('query', limitOrParams.query);
    }
    if (limitOrParams.healthStatus) {
      httpParams = httpParams.set('health_status', limitOrParams.healthStatus);
    }
    if (limitOrParams.subscriptionType) {
      httpParams = httpParams.set('subscription_type', limitOrParams.subscriptionType);
    }
    if (limitOrParams.approvalStatus) {
      httpParams = httpParams.set('approval_status', limitOrParams.approvalStatus);
    }
    if (limitOrParams.favoriteUuids?.length) {
      httpParams = httpParams.set('favorite_uuids', limitOrParams.favoriteUuids.join(','));
    }

    return this.httpClientService.get(`partners/list`, { params: httpParams });
  }

  getPartnerDashboard(limit: number, offset: number): Observable<SearchPartnersDbModel> {
    const params = new HttpParams()
      .set('limit', limit + '')
      .set('offset', offset + '');
    return this.httpClientService.get(`partners/dashboard`, { params });

  }

  // JB-675: cake-side feature-limit lookup, used by the create-role dialog
  // to show super-admins the picked partner's current_count / effective_limit
  // when they switch the partner picker. Mirrors the same source-of-truth
  // (FeatureHelper overlay) that nodejs hits before allowing the create.
  getCustomRolesFeatureLimit(partnerUuid: string): Observable<{
    partner_uuid: string;
    type: string;
    current_count: number;
    effective_limit: number;
    remaining: number;
    limit_reached: boolean;
  }> {
    return this.httpClientService.get(`partners/${partnerUuid}/feature-limits/custom_roles`, {});
  }

  getPartnersByName(name: string): Observable<any> {
    const body = { name: { value: name, operator: 'like' } };
    return this.httpClientService.post('partners/filter', body, {});
  }

  validatePartner(partnerValidationModel: PartnerValidationModel): Observable<{ isPartnerValid: PartnerValidationModel }> {
    return this.httpClientService.post('partners/validation', partnerValidationModel, {});
  }

  validatePartnerEmail(email: string): Observable<{ success: boolean }> {
    const body = {
      email,
      error_toast_in_interceptor: false
    };
    return this.httpClientService.v2Post('partners/check_email', body, {});
  }

  createPartner(partnerDbModel: PartnerDbModel) {
    return this.httpClientService.post('partners', partnerDbModel, {});
  }

  editPartner(partnerDbModel: Partial<PartnerDbModel>): Observable<{ partner: PartnerDbModel }> {
    const partnerUuid = partnerDbModel.uuid;
    const partnerDbModelBody = Object.assign({}, partnerDbModel);

    return this.httpClientService.put(`partners/${partnerUuid}`, partnerDbModelBody, {}).pipe(
      switchMap((result: { partner: PartnerDbModel }) => {
        this.setPartnerInLocalStorage(result.partner);
        return of(result);
      })
    );
  }

  /**
   * Update specific partner fields (for admin operations like changing registration mode)
   */
  updatePartner(partnerUuid: string, data: Partial<PartnerDbModel>): Observable<{ partner: PartnerDbModel }> {
    const context = new HttpContext().set(USE_UTC, true);
    return this.httpClientService.put(`partners/${partnerUuid}`, data, { context });
  }

  /**
   * Complete partner profile for SLG registrations
   * Uses dedicated endpoint that only allows SLG partners
   */
  completeProfile(data: Partial<PartnerDbModel>): Observable<{ partner: PartnerDbModel }> {
    return this.httpClientService.post('partners/complete_profile', data, {});
  }

  loadPartnerDataAfterLogin(partnerId: number): void {
    this.httpClientService.get(`partners/${partnerId}`, {}).subscribe({
      next: (result: { partner: PartnerDbModel }) => {
        this.setPartnerInLocalStorage(result.partner);
      },
    });
  }

  validateConfig(): Observable<ConfigValidationModel> {
    return this.httpClientService.get('partners/validate_config', {}).pipe(
      switchMap((result: ConfigValidationModel) => {
        this.setConfigValidationInLocalStorage(result);
        return of(result);
      })
    );
  }

  initializeDashboard(): Observable<{ partner: PartnerDbModel }> {
    const context = new HttpContext().set(USE_UTC, true);
    return this.httpClientService.get('workers/initialize_dashboard', { context }).pipe(
      switchMap((result: { partner: PartnerDbModel }) => {
        this.setPartnerInLocalStorage(result.partner);
        this.setPartnerCountryInLocalStorage(result.partner.country);
        this.featureService.setFeatureInLocalStorage(result.partner.subscription);
        this.setSubscriptionDetailInLocalStorage(result.partner.subscription);
        this.partnerSettingService.setInternalWidgetEnabled(!!+result.partner.is_internal_widget_enabled);
        this.partnerSettingService.setAppointmentAssistantEnabled(Number(result.partner.appointment_assistant_enabled) === 1);
        // Re-populate FeatureService in-memory cache so post-upgrade calls to
        // hasFeature() return fresh values without waiting for a page reload.
        this.featureService.loadSubscriptionFeatures(result.partner.subscription);
        this.featureService.loadFeaturesMasterList();
        // Notify banner / badge / quota components that hold cached subscription
        // views so they can re-read instead of showing pre-upgrade state.
        this.subscriptionRefreshedEvent.emit(result.partner.subscription);
        return of(result);
      })
    );
  }

  /**
   * Fetch a partner by UUID without any localStorage side effects.
   * Use this when resolving a "selected" partner that isn't in the currently
   * fetched partners-dashboard page, without overwriting the logged-in
   * partner session (`getPartner` does overwrite, which is wrong for pickers).
   */
  getPartnerByUuid(partnerUuid: string): Observable<PartnerDbModel> {
    const context = new HttpContext().set(USE_UTC, true);
    return this.httpClientService.get(`partners/${partnerUuid}`, { context }).pipe(
      switchMap((result: { partner: PartnerDbModel }) => of(result.partner)),
    );
  }

  getPartner(partnerId: string, refresh?: boolean): Observable<PartnerDbModel> {
    const context = new HttpContext().set(USE_UTC, true);
    if (!refresh) {
      const partner = this.getPartnerFromLocalStorage();
      if (partner) {
        return of(partner);
      }
    }

    return this.httpClientService.get(`partners/${partnerId}`, { context }).pipe(
      switchMap((result: { partner: PartnerDbModel }) => {
        this.setPartnerInLocalStorage(result.partner);
        return of(result.partner);
      })
    );
  }

  setLangInLocalStorage(partner: PartnerDbModel, loggedInWorker: WorkerDbModel) {
    const localStorageLang: string = this.localStorageService.get(LOCAL_STORAGE_CONSTANT.CURRENT_LANGUAGE_STATE_KEY);
    const finalLang: string = this.helperService.getDefaultLanguage(localStorageLang, partner, loggedInWorker);
    this.translate.setFallbackLang(finalLang);
    this.localStorageService.set(LOCAL_STORAGE_CONSTANT.CURRENT_LANGUAGE_STATE_KEY, finalLang);
  }

  uploadLogo(file: File, relativePath: string): Observable<{ partner: PartnerDbModel }> {
    const formData: FormData = new FormData();
    relativePath = relativePath.replace('.', Math.floor(Math.random() * 10000) + '.');
    formData.append('logo', file, relativePath);
    return this.httpClientService.post('partners/image', formData, {});
  }

  uploadLandingPageLogo(file: File, relativePath: string): Observable<{ partner: PartnerDbModel }> {
    const formData: FormData = new FormData();
    relativePath = relativePath.replace('.', Math.floor(Math.random() * 10000) + '.');
    formData.append('landingpage_logo', file, relativePath);
    return this.httpClientService.post('partners/image', formData, {});
  }

  uploadFaviconLogo(file: File, relativePath: string): Observable<{ partner: PartnerDbModel }> {
    const formData: FormData = new FormData();
    relativePath = relativePath.replace('.', Math.floor(Math.random() * 10000) + '.');
    formData.append('favicon_logo', file, relativePath);
    return this.httpClientService.post('partners/image', formData, {});
  }

  uploadPartnerEmailImage(file: File, relativePath: string): Observable<{ partner: PartnerDbModel }> {
    const formData: FormData = new FormData();
    relativePath = relativePath.replace('.', Math.floor(Math.random() * 10000) + '.');
    formData.append('email_logo', file, relativePath);
    return this.httpClientService.post('partners/image', formData, {});
  }

  uploadPartnerBookingPageBackgroundImage(
    file: File, relativePath: string
  ): Observable<{ partner: PartnerDbModel }> {
    const formData: FormData = new FormData();
    relativePath = relativePath.replace('.', Math.floor(Math.random() * 10000) + '.');
    formData.append('booking_page_background_logo', file, relativePath);
    return this.httpClientService.post('partners/image', formData, {});
  }

  setPartnerInLocalStorage(partner: PartnerDbModel): void {
    const encryptedString = this.cryptoService.encryptValue(
      JSON.stringify(partner)
    );
    this.localStorageService.set(LOCAL_STORAGE_CONSTANT.PARTNER_KEY, encryptedString);
  }

  setConfigValidationInLocalStorage(configValidation: ConfigValidationModel): void {
    const encryptedString = this.cryptoService.encryptValue(
      JSON.stringify(configValidation)
    );
    this.localStorageService.set(LOCAL_STORAGE_CONSTANT.CONFIG_VALIDATION, encryptedString);
    LoggerService.log('config validation saved');
  }

  getPartnerFromLocalStorage(): PartnerDbModel {
    const encryptedPartner: string = this.localStorageService.get(LOCAL_STORAGE_CONSTANT.PARTNER_KEY);
    if (encryptedPartner) {
      const decryptedPartner: string = this.cryptoService.decryptValue(encryptedPartner);
      const partner: PartnerDbModel = Object.assign({}, JSON.parse(decryptedPartner));
      if (partner.internal_token === undefined) {
        this.initializeDashboard().subscribe(partner => {
          return partner;
        });
      } else {
        if (!partner.supported_widget_languages) {
          partner.supported_widget_languages = [];
        }
        return partner;
      }
    } else {
      return null;
    }
  }

  setSubscriptionDetailInLocalStorage(subscription: SubscriptionModel): void {
    const encryptedString = this.cryptoService.encryptValue(
      JSON.stringify(subscription)
    );
    this.localStorageService.set(LOCAL_STORAGE_CONSTANT.SUBSCRIPTION_DETAIL, encryptedString);
  }

  getSubscriptionDetailFromLocalStorage(): SubscriptionModel {
    const encrypted: string = this.localStorageService.get(LOCAL_STORAGE_CONSTANT.SUBSCRIPTION_DETAIL);
    if (encrypted) {
      const decrypted: string = this.cryptoService.decryptValue(encrypted);
      const subscription: SubscriptionModel = Object.assign({}, JSON.parse(decrypted));
      return subscription;
    } else {
      return null;
    }
  }

  getConfigValidationFromLocalStorage(): ConfigValidationModel {
    const encrypted: string = this.localStorageService.get(LOCAL_STORAGE_CONSTANT.CONFIG_VALIDATION);
    if (encrypted) {
      const decrypted: string = this.cryptoService.decryptValue(encrypted);
      const configValidation: ConfigValidationModel = Object.assign({}, JSON.parse(decrypted));
      return configValidation;
    } else {
      return null;
    }
  }

  getPartnerUuid(): string {
    const partnerDb = this.getPartnerFromLocalStorage();
    return partnerDb?.uuid || null;
  }

  updateTranslationInfoInPartner(partnerTranslationForm: PartnerTranslationForm, partner: PartnerDbModel, currentLang: string) {
    partner.is_multi_language = partnerTranslationForm.is_multi_language ? 1 : 0;
    APP_CONSTANTS.LANGUAGES.forEach(language => {
      if (partner.is_multi_language) {
        if (language.locale === currentLang) {
          partner.micro_partner_description = partnerTranslationForm['formControl_' + language.value].description;
        }
        partner['micro_partner_description_' + language.value] = partnerTranslationForm['formControl_' + language.value].description;
      } else {
        partner.micro_partner_description = partnerTranslationForm.formControl_default.description;
        partner['micro_partner_description_' + language.value] = undefined;
      }
    });
  }

  fixPartnerData(
    partnerDbModel: PartnerDbModel,
    useLandingPageAuth = false,
    authTokenUuid = ''
  ): Observable<{ partner: PartnerDbModel }> {
    const partnerId = partnerDbModel.uuid;
    delete partnerDbModel.id;
    const partnerDbModelBody: PartnerDbModel = Object.assign({}, partnerDbModel);

    let context: HttpContext;
    if (useLandingPageAuth) {
      context = new HttpContext().set(LANDING_PAGE_AUTH_REQUIRED, authTokenUuid);
    }

    return this.httpClientService.put(`partners/${partnerId}`, partnerDbModelBody, { context }).pipe(
      switchMap((result: { partner: PartnerDbModel }) => {
        return of(result);
      })
    );
  }

  requestDigitalizationSuite(payload: DigitizationSuiteFormModel): Observable<any> {
    return this.httpClientService.post('partners/request_digitalization_suite', payload, {});
  }

  getSetupAssistantStatus(): Observable<SetupAssistantStatusModel> {
    return this.httpClientService.get('partners/setup_assistant_status', {});
  }

  getPartnerClientIds(type: string, query: string, bookingName: string = undefined): Observable<IPartnerClientId[]> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('type', type);
    query && (queryParams = queryParams.append('query', query));
    bookingName && (queryParams = queryParams.append('booking_name', bookingName));

    return this.httpClientService.authServiceGet(`partners/client_ids`, {
      params: queryParams
    });
  }

  setPartnerCountryInLocalStorage(country: CountryDbModel): void {
    const encryptedString = this.cryptoService.encryptValue(
      JSON.stringify(country)
    );
    this.localStorageService.set(LOCAL_STORAGE_CONSTANT.CALENSO_PARTNER_COUNTRY, encryptedString);
    LoggerService.log('partner country saved');
  }

  getPartnerCountryFromLocalStorage(): CountryDbModel {
    const encryptedPartner: string = this.localStorageService.get(LOCAL_STORAGE_CONSTANT.CALENSO_PARTNER_COUNTRY);
    if (encryptedPartner) {
      const decryptedPartner: string = this.cryptoService.decryptValue(encryptedPartner);
      const partner: CountryDbModel = Object.assign({}, JSON.parse(decryptedPartner));
      return partner;
    } else {
      return null;
    }
  }

  blockPartner(partnerUuid: string): Observable<{ success: boolean }> {
    return this.httpClientService.post('partners/block', { partner_uuid: partnerUuid }, {});
  }

  approvePartnerRegistrationRequest(partnerUuid: string): Observable<{
    success: boolean,
    verification_email?: 'sent' | 'skipped' | 'skipped_already_verified' | 'skipped_resend_limit' | 'failed'
  }> {
    return this.httpClientService.post('partners/approve', { partner_uuid: partnerUuid }, {});
  }

  clonePartner(param: PartnerCloneRequest): Observable<{ success: boolean }> {
    return this.httpClientService.authServicePost('partners/clone', param, {});
  }

  importCompanyWideOffice365Calendar(): Observable<{ success: boolean }> {
    return this.httpClientService.get(`partners/import_office365_calendars_all`, {});
  }

  importCompanyWideOffice365Teams(): Observable<{ success: boolean }> {
    return this.httpClientService.get(`partners/import_teams_meeting_authorizations_all`, {});
  }
}
